import { ElementNotFound } from "@senciamatch/frontend/components/ElementNotFound";
import { LoadingData } from "@senciamatch/frontend/components/LoadingData";
import { groupBy } from "@senciamatch/frontend/libs/array";
import { getJapaneseDayOfWeek } from "@senciamatch/frontend/libs/getJapaneseDayOfWeek";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import type { WorkShiftGroup } from "@senciamatch/shared/models/workShiftGroup";
import React from "react";
import { v7 as uuidv7 } from "uuid";
import { Cell } from "./Cell";

type Props = {
  workShiftGroup: WorkShiftGroup;
};

const GroupByBuilding: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (props, ref) => {
  const { workShiftGroup } = props;
  const getWorkShifts = trpc.getStaffWorkShiftsByGroupId.useQuery({ workShiftGroupId: workShiftGroup.id });
  const getBuildings = trpc.getBuildings.useQuery();

  if (getWorkShifts.isPending || getBuildings.isPending) return <LoadingData />;
  if (!getWorkShifts.data || !getBuildings.data) return <ElementNotFound />;

  const workShifts = getWorkShifts.data.filter((workShift) => workShift.buildingId != null);
  const workShiftsGroupedByBuildings = groupBy(workShifts, (workShift) => workShift.buildingId as string);

  // Create maps for buildingId to companyId and companyName
  const buildingCompanyMap = new Map<string, string>();
  const buildingCompanyNameMap = new Map<string, string>();
  getBuildings.data.buildings.forEach((building) => {
    buildingCompanyMap.set(building.id, building.companyId);
    buildingCompanyNameMap.set(building.id, building.companyName);
  });

  // Sort buildings by companyId
  const sortedBuildingEntries = Object.entries(workShiftsGroupedByBuildings).sort((a, b) => {
    const companyIdA = buildingCompanyMap.get(a[0]) || "";
    const companyIdB = buildingCompanyMap.get(b[0]) || "";
    return companyIdA.localeCompare(companyIdB);
  });

  const lastDayOfMonth = new Date(workShiftGroup.year, workShiftGroup.month, 0).getDate();

  return (
    <div className="h-full overflow-auto ml-8 mt-8" ref={ref}>
      <table className="w-[2450px] border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="sticky top-0 left-0 bg-gray-100 p-2 border border-gray-200 z-40 min-w-[140px]">建物名</th>
            {Array.from({ length: lastDayOfMonth }, (_, i) => {
              const day = i + 1;
              const dayOfWeek = getJapaneseDayOfWeek(workShiftGroup.year, workShiftGroup.month, day);
              const bgColor = dayOfWeek === "日" ? "bg-red-100" : dayOfWeek === "土" ? "bg-blue-100" : "bg-gray-100";
              return (
                <th key={uuidv7()} className={`sticky top-0 ${bgColor} p-2 border border-gray-200 z-10 w-[270px]`}>
                  {day}
                  <br />
                  {dayOfWeek}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedBuildingEntries.map(([buildingId, workShifts]) => {
            return (
              <tr key={buildingId} className="border border-gray-200 h-20 bg-white">
                <td className="sticky left-0 bg-white border z-20 border-gray-200 p-2 min-w-[140px]">
                  {workShifts[0].buildingName}
                  <br />
                  <span className="text-xs text-gray-500">
                    {buildingCompanyNameMap.get(buildingId)} ({buildingCompanyMap.get(buildingId)})
                  </span>
                </td>
                {Array.from({ length: lastDayOfMonth }, (_, i) => {
                  const dayWorkShifts = workShifts.filter((workShift) => workShift.day === i + 1);
                  return <Cell key={uuidv7()} workShiftCells={dayWorkShifts} />;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default React.forwardRef(GroupByBuilding);
